<template>
  <base-list-view
    @state-scroll="scrollList"
  >
    <div class="speaker-list">
      <pre-loader v-if="isLoadingPrev"/>
      <div
        v-for="speaker in speakersList"
        :key="speaker.id"
        :id="'speaker-' + speaker.id"
        class="speaker-item"
        @click="saveScrollContext(speaker)"
        @keydown.enter="saveScrollContext(speaker)"
      >
        <speaker-card
          :id="speaker.id"
          :photo="speaker.photo"
          :name="speaker.name"
          :company="speaker.company"
          :position="speaker.position"
          :description="speaker.description"
        />
      </div>
      <system-card
        v-if="systemMessage"
        :system-message="systemMessage"
        :cbFunc="repeatGetPage"
      />
      <pre-loader v-if="isLoading && !isLoadingPrev"/>
    </div>
  </base-list-view>
</template>
<script>
import { DateTime } from 'luxon';
import appBaseNav from '@/components/mixins/appBaseNav';
import speackers from '@/components/mixins/methodsSpeackers';
import banners from '@/components/mixins/methodsBanners';
import listScrollMixin from '@/components/mixins/viewers/listScrollMixin';

import baseListView from '@/components/shared/baseListView.vue';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';

export default {
  name: 'ListSpeakersView',
  components: { baseListView, SystemCard },
  props: {
    launchDownload: {
      type: Boolean,
    },
    locale: {
      type: String,
    },
    page: {
      type: String,
      default: null,
    },
  },
  data() {
    const savedRaw = sessionStorage.getItem('speakerScrollCtx');
    const saved = savedRaw ? JSON.parse(savedRaw) : null;
    if (saved) sessionStorage.removeItem('speakerScrollCtx');
    const startPage = saved ? saved.firstPage : (parseInt(this.page, 10) || 1);
    return {
      speakersList: [],
      isLoading: true,
      firstPage: startPage,
      curPage: startPage,
      countPage: null,

      isLoadingPrev: false,
      isLastDownloaded: false,
      systemMessage: null,
      scrollTargetId: saved ? saved.targetId : null,
    };
  },
  mixins: [appBaseNav, speackers, banners, listScrollMixin],
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('programList.header_title'));
    },
  },
  computed: {

  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('speakerList.header_title'));
    Promise.all([
      this.getBanner('speaker', this.$store.getters.getBannerSpeakerTs),
    ])
      .then(([
        banner,
      ]) => {
        this.isLoading = false;
        if (banner && Object.keys(banner).length) {
          this.$emit('showBanner', banner);
          const nowMilis = DateTime.now().toMillis();
          localStorage.setItem('bannerSpeakerTs', nowMilis);
          this.$store.commit('setBannerSpeakerTs', nowMilis);
        }
        this.getPage();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  },
  methods: {
    getPage() {
      if (this.countPage === null || this.curPage <= this.countPage) {
        this.isLoading = true;

        this.getSpeackersList(this.curPage)
          .then((data) => {
            this.isLoading = false;
            this.$emit('turnOff', {
              stateDownload: false,
              stateCheckInbox: false,
            });
            if (data?.results && Array.isArray(data.results)) {
              const tagged = data.results.map((s) => ({ ...s, sourcePage: data.page }));
              this.speakersList = [...this.speakersList, ...tagged];
              this.countPage = data.num_pages;
              this.curPage = data.page;
              this.isLastDownloaded = true;
              this.systemMessage = null;
              this.$nextTick(() => {
                const hadTarget = !!this.scrollTargetId;
                this.tryScrollToTarget();
                if (!hadTarget) this.checkScrollBoundaries();
              });
            } else {
              this.isLastDownloaded = false;
              this.systemMessage = this.$t('system.bad_connect');
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('getPage()::', error);
          });
      }
    },
    getPrevPage() {
      if (this.firstPage <= 1) return;

      const scrollEl = document.getElementById('content__wrapper');
      const listEl = document.getElementById('listView');

      const heightBeforeLoader = listEl.offsetHeight;
      const scrollBeforeLoader = scrollEl.scrollTop;

      this.isLoading = true;
      this.isLoadingPrev = true;

      this.$nextTick(() => {
        this.adjustScroll(scrollBeforeLoader, heightBeforeLoader);
      });

      this.getSpeackersList(this.firstPage - 1)
        .then((data) => {
          if (data?.results && Array.isArray(data.results)) {
            const heightBeforeData = listEl.offsetHeight;
            const scrollBeforeData = scrollEl.scrollTop;

            this.isLoading = false;
            this.isLoadingPrev = false;
            const tagged = data.results.map((s) => ({ ...s, sourcePage: data.page }));
            this.speakersList = [...tagged, ...this.speakersList];
            this.firstPage -= 1;
            this.systemMessage = null;

            this.$nextTick(() => {
              this.adjustScroll(scrollBeforeData, heightBeforeData);
            });
          } else {
            this.isLoading = false;
            this.isLoadingPrev = false;
            this.systemMessage = this.$t('system.bad_connect');
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('getPrevPage()::', error);
          this.isLoading = false;
          this.isLoadingPrev = false;
        });
    },
    saveScrollContext(speaker) {
      sessionStorage.setItem('speakerScrollCtx', JSON.stringify({
        targetId: `speaker-${speaker.id}`,
        firstPage: speaker.sourcePage || 1,
      }));
    },
    tryScrollToTarget() {
      if (!this.scrollTargetId) return;
      const el = document.getElementById(this.scrollTargetId);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        this.scrollTargetId = null;
      }
    },
  },
};
</script>
<style lang="scss">
.speaker {
  &-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  &-list &-item {
    flex: 1 1 140px;
    max-width: calc(50% - 6px);
    @media (min-width: 480px) {
      flex: 1 1 180px;
      max-width: calc(33.333% - 6px);
    }

    & .upCard {
      border-radius: 16px;
    }
  }
}
</style>
